import { Heart, Loader2 } from 'lucide-react';
import { use_favourite_vendors, use_toggle_favourite_vendor } from '@/features/favourite/hooks';
import { cn } from '@/lib/utils';

/**
 * Heart toggle for favouriting a vendor.
 *
 * Reads the favourites list rather than taking an `is_favourited` prop, so the same button is
 * correct wherever it's dropped in without the surrounding screen having to plumb the state
 * through. The list is small (a person's own saved vendors) and React Query dedupes it, so the
 * extra read costs nothing in practice.
 *
 * Signed-out users are shown nothing rather than a heart that 401s on tap.
 */
export function FavouriteVendorButton({
  vendor_id,
  vendor_name,
}: {
  vendor_id: string;
  vendor_name?: string | null;
}) {
  const favourites_q = use_favourite_vendors();
  const toggle = use_toggle_favourite_vendor();

  // A 401/403 means "not signed in" — hide rather than offer an action that cannot work.
  if (favourites_q.isError) return null;

  const favourited = (favourites_q.data ?? []).some((f) => f.vendor.id === vendor_id);
  const label = favourited
    ? `Remove ${vendor_name ?? 'this vendor'} from favourites`
    : `Save ${vendor_name ?? 'this vendor'} to favourites`;

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={favourited}
      title={label}
      disabled={toggle.isPending || favourites_q.isLoading}
      onClick={() => toggle.mutate({ vendor_id, favourited })}
      data-testid={`favourite-vendor-${vendor_id}`}
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        'border-brand-outline-variant/25 hover:bg-secondary/50 disabled:opacity-60',
        favourited && 'border-transparent bg-rose-50 dark:bg-rose-950/40',
      )}
    >
      {toggle.isPending ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <Heart
          className={cn('h-5 w-5', favourited ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground')}
        />
      )}
    </button>
  );
}
